import { isOptimizelyVariationEnabled } from './useOptimizelyVariation';

/**
 * The single Prove prefill feature key. M1/M2 (entry Prove at /apply/landing) and M3 (PROVE_STEP
 * inside app intake, XF-10207) are different RULES on this one flag — never a second flag.
 */
export const PROVE_PREFILL_FLAG = 'ece_prove_prefill';

/**
 * Variation keys of the M3 audience-gated rule, exactly as published in Optimizely (XF-10213 owns the
 * rule and its audiences). These constants are the frontend's side of that contract: a decision whose
 * key is not one of them is NOT recognised as M3, and any key outside the disabled list counts as the
 * legacy entry Prove being on. Rename them only together with the Optimizely variations.
 */
export const PROVE_PREFILL_M3_VARIATIONS = {
  test: 'm3_prove_step',
  control: 'm3_prove_step_control',
} as const;

/** Channel codes PROVE_STEP applies to; everything else (ISO, strategic, renewals) never sees it. */
export const PROVE_STEP_CHANNEL_CODES = ['DIRECT', 'MARKETING', 'PARTNER_REFERRAL'] as const;

export type ProvePrefillDecision = {
  /** The M3 rule produced this decision: entry Prove (ProveBasicInfo + /login/mobile) is retired. */
  isM3Rule: boolean;
  /** Applicant is in the M3 test bucket: PROVE_STEP renders as the first intake step. */
  inProveStep: boolean;
  /** Legacy M1/M2 entry Prove is on for this applicant (ProveBasicInfo at /apply/landing). */
  legacyEntryProve: boolean;
};

export const getProvePrefillDecision = (variation: string | null | undefined): ProvePrefillDecision => {
  const key = variation?.trim().toLowerCase();
  const m3Keys: readonly string[] = Object.values(PROVE_PREFILL_M3_VARIATIONS);
  const isM3Rule = !!key && m3Keys.includes(key);

  return {
    isM3Rule,
    inProveStep: key === PROVE_PREFILL_M3_VARIATIONS.test,
    legacyEntryProve: !isM3Rule && isOptimizelyVariationEnabled(variation),
  };
};

export const isProveStepChannel = (channelCode?: string | null): boolean =>
  !!channelCode && (PROVE_STEP_CHANNEL_CODES as readonly string[]).includes(channelCode);

/**
 * The M3 rule's audiences match on the `ondeck_salesChannel` attribute (MARKETING, PARTNER_REFERRAL,
 * DIRECT), so every ece_prove_prefill decision must carry it or the applicant falls out of the rule.
 */
export const provePrefillAttributes = (channelCode?: string | null): Record<string, string> =>
  channelCode ? { ondeck_salesChannel: channelCode } : {};
